// ============================================================
// ADMINS PAGE — admin staff list + create + lifecycle actions
// ============================================================

import React, { useState, useEffect } from 'react';
import { adminProfileAPI } from '../services/api';
import {
  PageHeader, DataTable, AvatarCell,
  ActionBtn, Modal, FormField, Input
} from '../components/Shared';
import toast from 'react-hot-toast';
import styles from './Doctors.module.css';

const AVATAR_COLORS = ['#C0185A','#1E3A5F','#0D9A6A','#E07B2A','#6B46C1','#2B6CB0'];
function avatarColor(i) { return AVATAR_COLORS[i % AVATAR_COLORS.length]; }

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  iban: '',
};

export default function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add-admin modal state
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Mark-leaver modal state — separate from add-admin so they can't collide
  const [leaverTarget, setLeaverTarget] = useState(null); // the row being retired
  const [leaverReason, setLeaverReason] = useState('');
  const [leaverSubmitting, setLeaverSubmitting] = useState(false);

  /* @admin-edit-ui */
  const [editTarget, setEditTarget] = useState(null);
  /* @admin-onboarding-edit */
  const [editForm, setEditForm] = useState({ firstName:'', lastName:'', phone:'', iban:'', email:'' });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Per-row resend-setup state — track which admin we're currently resending
  // for so the right button shows a spinner without freezing the whole UI
  const [resendingId, setResendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminProfileAPI.getAll();
      setAdmins(data.data);
    } catch { toast.error('Αδυναμία φόρτωσης διαχειριστών'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        // IBAN is optional — only send if filled, so DB keeps NULL otherwise
        ...(form.iban.trim() && { iban: form.iban.trim().replace(/\s+/g, '').toUpperCase() }),
      };
      await adminProfileAPI.create(payload);
      toast.success('✅ Ο διαχειριστής δημιουργήθηκε. Στάλθηκε email ορισμού κωδικού.');
      setShowAdd(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Σφάλμα δημιουργίας διαχειριστή';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (admin) => {
    setResendingId(admin.adminId);
    try {
      await adminProfileAPI.resendSetup(admin.adminId);
      toast.success('✅ Στάλθηκε νέο email ορισμού κωδικού.');
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Σφάλμα αποστολής';
      toast.error(msg);
    } finally {
      setResendingId(null);
    }
  };

  const handleMarkLeaver = async () => {
    if (!leaverTarget) return;
    setLeaverSubmitting(true);
    try {
      const payload = {
        ...(leaverReason.trim() && { reason: leaverReason.trim() }),
      };
      await adminProfileAPI.markLeaver(leaverTarget.adminId, payload);
      toast.success('✅ Ο διαχειριστής ορίστηκε ως αποχωρήσας.');
      setLeaverTarget(null);
      setLeaverReason('');
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Σφάλμα ενέργειας';
      toast.error(msg);
    } finally {
      setLeaverSubmitting(false);
    }
  };

  const COLS = [
    { key:'name', label:'Διαχειριστής', width:'2fr', render: (r,i) => (
      <AvatarCell
        initials={`${r.firstName?.[0] || '?'}${r.lastName?.[0] || ''}`}
        name={`${r.firstName || ''} ${r.lastName || ''}`}
        sub={r.email}
        color={avatarColor(i)}
      />
    )},
    { key:'phone', label:'Τηλέφωνο', width:'1.2fr', render: r => r.phone || '—' },
    { key:'iban',  label:'IBAN',     width:'1.3fr', render: r => r.iban || '—' },
    { key:'state', label:'Κατάσταση', width:'1fr', render: r => {
      // Three states for an admin staff row:
      //   1) Leaver — explicitly retired
      //   2) Onboarding — created but hasn't set their password yet
      //   3) Active — verified, fully onboarded
      if (r.isLeaver) {
        return <span className="pill" style={{ background: '#FEE2E2', color: '#991B1B' }}>Αποχωρήσας</span>;
      }
      if (!r.isVerified) {
        return <span className="pill" style={{ background: '#FEF3C7', color: '#92400E' }}>📋 Onboarding</span>;
      }
      return <span className="pill pill-green">● Ενεργός</span>;
    }},
    { key:'created', label:'Δημιουργία', width:'0.9fr', render: r => r.createdAt ? new Date(r.createdAt).toLocaleDateString('el-GR') : '—' },
    { key:'actions', label:'', width:'200px', render: r => {
      // Action buttons depend on state. Leavers get nothing — they're terminal.
      // Onboarding-state rows get a "resend link" button. Active rows get a
      // "mark leaver" (Αποχώρηση) button.
      if (r.isLeaver) return null;

      if (!r.isVerified) {
        return (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <ActionBtn variant="default" onClick={() => openEdit(r)}>Επεξεργασία</ActionBtn>
            <ActionBtn variant="default" onClick={() => handleResend(r)} disabled={resendingId === r.adminId}>
              {resendingId === r.adminId ? 'Αποστολή...' : 'Επαναποστολή link'}
            </ActionBtn>
            <ActionBtn variant="default" onClick={() => { setLeaverTarget(r); setLeaverReason(''); }}>Διαγραφή</ActionBtn>
          </div>
        );
      }

      return (
        <div style={{ display:'flex', gap:8 }}>
          <ActionBtn variant="default" onClick={() => openEdit(r)}>Επεξεργασία</ActionBtn>
          <ActionBtn variant="default" onClick={() => { setLeaverTarget(r); setLeaverReason(''); }}>Αποχώρηση</ActionBtn>
        </div>
      );
    }},
  ];

  const openEdit = (r) => {
    setEditForm({ firstName: r.firstName || '', lastName: r.lastName || '', phone: r.phone || '', iban: r.iban || '', email: r.email || '' });
    setEditTarget(r);
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setEditSubmitting(true);
    try {
      await adminProfileAPI.update(editTarget.adminId, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        phone: editForm.phone.trim(),
        iban: editForm.iban.trim() || null,
        // email only sent when onboarding (backend rejects it post-activation)
        ...(!editTarget.isVerified ? { email: editForm.email.trim() } : {}),
      });
      toast.success('✅ Τα στοιχεία ενημερώθηκαν.');
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Αποτυχία ενημέρωσης.');
    } finally {
      setEditSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Διαχειριστές"
        subtitle={`${admins.length} διαχειριστές στην ομάδα`}
        action={<ActionBtn variant="primary" onClick={() => setShowAdd(true)}>+ Προσθήκη Διαχειριστή</ActionBtn>}
      />

      {loading ? <div className={styles.loading}>Φόρτωση...</div> : (
        admins.length === 0 ? (
          <div className={styles.allGood}>
            <div style={{fontSize:48,marginBottom:14}}>👥</div>
            <div style={{fontSize:16,fontWeight:700,color:'var(--navy)'}}>Δεν υπάρχουν διαχειριστές ακόμα</div>
            <div style={{fontSize:13,color:'var(--muted)',marginTop:6}}>Πατήστε "Προσθήκη Διαχειριστή" για να ξεκινήσετε</div>
          </div>
        ) : (
          <DataTable columns={COLS} rows={admins} />
        )
      )}

      {/* Add admin modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Προσθήκη Νέου Διαχειριστή" size="md">
        <div style={{ padding: '0 4px 4px', fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Δημιουργείτε τον λογαριασμό. Ο νέος διαχειριστής θα λάβει email με σύνδεσμο για να ορίσει κωδικό πρόσβασης (ισχύει 7 ημέρες).
        </div>
        <div className={styles.formGrid}>
          <FormField label="Όνομα" required>
            <Input placeholder="π.χ. Νικόλας" value={form.firstName} onChange={e => setForm({...form, firstName:e.target.value})} />
          </FormField>
          <FormField label="Επώνυμο" required>
            <Input placeholder="π.χ. Σαΐμον" value={form.lastName} onChange={e => setForm({...form, lastName:e.target.value})} />
          </FormField>
          <FormField label="Email" required>
            <Input type="email" placeholder="onoma@email.com" value={form.email} onChange={e => setForm({...form, email:e.target.value})} />
          </FormField>
          <FormField label="Τηλέφωνο" required>
            <Input placeholder="+306900000000" value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} />
          </FormField>
          <div style={{gridColumn:'1/-1'}}>
            <FormField label="IBAN (προαιρετικό)">
              <Input placeholder="GR..." value={form.iban} onChange={e => setForm({...form, iban:e.target.value})} />
            </FormField>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <ActionBtn variant="default" onClick={() => setShowAdd(false)} disabled={submitting}>Ακύρωση</ActionBtn>
          <ActionBtn
            variant="primary"
            onClick={handleCreate}
            disabled={!form.firstName || !form.lastName || !form.email || !form.phone || submitting}
          >
            {submitting ? 'Δημιουργία...' : 'Δημιουργία Λογαριασμού'}
          </ActionBtn>
        </div>
      </Modal>

      {/* @admin-edit-ui edit modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Επεξεργασία Διαχειριστή" size="md">
        <div className={styles.formGrid}>
          <FormField label="Όνομα" required>
            <Input value={editForm.firstName} onChange={e => setEditForm({...editForm, firstName:e.target.value})} />
          </FormField>
          <FormField label="Επώνυμο" required>
            <Input value={editForm.lastName} onChange={e => setEditForm({...editForm, lastName:e.target.value})} />
          </FormField>
          <FormField label="Email">
            {editTarget && !editTarget.isVerified
              ? <Input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email:e.target.value})} />
              : <Input type="email" value={editTarget?.email || ''} disabled readOnly />}
          </FormField>
          <FormField label="Τηλέφωνο" required>
            <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone:e.target.value})} />
          </FormField>
          <div style={{gridColumn:'1/-1'}}>
            <FormField label="IBAN (προαιρετικό)">
              <Input placeholder="GR..." value={editForm.iban} onChange={e => setEditForm({...editForm, iban:e.target.value})} />
            </FormField>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <ActionBtn variant="default" onClick={() => setEditTarget(null)} disabled={editSubmitting}>Ακύρωση</ActionBtn>
          <ActionBtn variant="primary" onClick={handleEdit} disabled={!editForm.firstName || !editForm.lastName || !editForm.phone || editSubmitting}>
            {editSubmitting ? 'Αποθήκευση...' : 'Αποθήκευση'}
          </ActionBtn>
        </div>
      </Modal>

      {/* Mark-leaver modal — confirms intent + collects optional reason for audit */}
      <Modal
        open={!!leaverTarget}
        onClose={() => { setLeaverTarget(null); setLeaverReason(''); }}
        title="Αποχώρηση Διαχειριστή"
        size="md"
      >
        {leaverTarget && (
          <>
            <div style={{ padding: '0 4px 4px', fontSize: 14, color: 'var(--navy)', marginBottom: 8 }}>
              Πρόκειται να ορίσετε ως αποχωρήσαντα τον διαχειριστή <strong>{leaverTarget.firstName} {leaverTarget.lastName}</strong> ({leaverTarget.email}).
            </div>
            <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400E', marginBottom: 16 }}>
              ⚠️ Αυτή η ενέργεια θα τερματίσει άμεσα τις ενεργές συνεδρίες του και θα του αφαιρέσει την πρόσβαση στην πύλη. Δεν είναι αντιστρεπτή από αυτή τη σελίδα.
            </div>
            <FormField label="Λόγος αποχώρησης (προαιρετικό)">
              <textarea
                className={styles.textarea}
                rows={3}
                placeholder="π.χ. Αλλαγή ρόλου, εθελοντική παραίτηση, λήξη σύμβασης..."
                value={leaverReason}
                onChange={e => setLeaverReason(e.target.value)}
              />
            </FormField>
            <div className={styles.modalFooter}>
              <ActionBtn
                variant="default"
                onClick={() => { setLeaverTarget(null); setLeaverReason(''); }}
                disabled={leaverSubmitting}
              >
                Ακύρωση
              </ActionBtn>
              <ActionBtn
                variant="primary"
                onClick={handleMarkLeaver}
                disabled={leaverSubmitting}
              >
                {leaverSubmitting ? 'Αποθήκευση...' : 'Επιβεβαίωση Αποχώρησης'}
              </ActionBtn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}